import React, { useEffect, useRef, useState, useMemo } from 'react';
import { App } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listSortiesCount, listMySortiesCount } from '../api/sorties';
import { listIssuesCount, listMyIssuesCount } from '../api/issues';
import { listMyRoomReportsCount, listRoomReportsCount } from '../api/roomReports';
import { initNotificationSounds, playUrgentSound, playWarningSound } from '../utils/notificationSounds';
import { LogoutIcon } from './UIKit';
import { isPageVisible } from '../utils/pageVisibility';
import AiChatbot from './AiChatbot';
import '../styles/global.css';

// ── Nav icons (lightweight inline SVGs) ──────────────────────
const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const ListIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" x2="21" y1="6" y2="6"/><line x1="8" x2="21" y1="12" y2="12"/><line x1="8" x2="21" y1="18" y2="18"/>
    <line x1="3" x2="3.01" y1="6" y2="6"/><line x1="3" x2="3.01" y1="12" y2="12"/><line x1="3" x2="3.01" y1="18" y2="18"/>
  </svg>
);
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22V12h6v10"/>
    <path d="M8 7h.01"/><path d="M12 7h.01"/><path d="M16 7h.01"/>
    <path d="M8 11h.01"/><path d="M12 11h.01"/><path d="M16 11h.01"/>
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="16" y1="2" y2="6"/>
    <line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/>
  </svg>
);
const ProfileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const QrCodeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="5" height="5" x="3" y="3" rx="1"/><rect width="5" height="5" x="16" y="3" rx="1"/>
    <rect width="5" height="5" x="3" y="16" rx="1"/>
    <path d="M21 16h-3a2 2 0 0 0-2 2v3"/><path d="M21 21v.01"/>
    <path d="M12 7v3a2 2 0 0 1-2 2H7"/>
    <path d="M12 3h.01"/><path d="M12 16v.01"/><path d="M16 12h1"/>
  </svg>
);
const BotNavIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <circle cx="12" cy="5" r="2"/><line x1="12" y1="7" x2="12" y2="11"/>
    <line x1="8" y1="15" x2="8" y2="15" strokeWidth="3"/><line x1="16" y1="15" x2="16" y2="15" strokeWidth="3"/>
  </svg>
);
const HamburgerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" x2="21" y1="6" y2="6"/><line x1="3" x2="21" y1="12" y2="12"/><line x1="3" x2="21" y1="18" y2="18"/>
  </svg>
);

// ── NavLink ───────────────────────────────────────────────────
function NavLink({ path, icon, label, redBadge = 0, orangeBadge = 0, currentPath, onClick }) {
  const isActive = currentPath === path || (path !== '/' && currentPath.startsWith(path) && path.split('/').length >= currentPath.split('/').length);
  const exactActive = currentPath === path;

  return (
    <button
      className={`nav-link${exactActive ? ' active' : ''}`}
      onClick={() => onClick(path)}
    >
      {icon}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {redBadge > 0 && <span className="nav-badge nav-badge-red">{redBadge > 99 ? '99+' : redBadge}</span>}
      {orangeBadge > 0 && <span className="nav-badge nav-badge-orange">{orangeBadge > 99 ? '99+' : orangeBadge}</span>}
    </button>
  );
}

// ── AppLayout ─────────────────────────────────────────────────
export default function AppLayout() {
  const { user, logout } = useAuth();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  const [badgeCounts, setBadgeCounts] = useState({
    supervisorPendingSorties: 0,
    supervisorOverdueSorties: 0,
    supervisorActiveIssues: 0,
    supervisorPendingReports: 0,
    studentPendingSorties: 0,
    studentActiveIssues: 0,
    studentPendingReports: 0,
  });

  const supervisorPrevRef = useRef({ overdue: 0, pending: 0, issues: 0, reports: 0, didNotify: false });

  // Badge refresh (keep original logic)
  useEffect(() => {
    if (!user?.role) return;
    initNotificationSounds();
    let cancelled = false;
    const refreshMs = Number(process.env.REACT_APP_BADGE_REFRESH_MS || 30 * 1000);

    const refresh = async () => {
      try {
        if (user.role === 'supervisor') {
          const [sortiesPendingRes, sortiesOverdueRes, issuesRes] = await Promise.all([
            listSortiesCount({ statut: 'PENDING' }),
            listSortiesCount({ overdue: true }),
            listIssuesCount({ statut_in: 'OPEN,IN_PROGRESS' }),
          ]);
          const supervisorPendingSorties = Number(sortiesPendingRes?.total || 0);
          const supervisorOverdueSorties = Number(sortiesOverdueRes?.total || 0);
          const supervisorActiveIssues = Number(issuesRes?.total || 0);
          const reportsRes = await listRoomReportsCount({ status: 'SIGNED_BY_STUDENT' });
          const supervisorPendingReports = Number(reportsRes?.total || 0);

          const prev = supervisorPrevRef.current;
          const isFirstRefresh = !prev.didNotify;
          const hasIncrease = supervisorOverdueSorties > prev.overdue || supervisorPendingSorties > prev.pending || supervisorActiveIssues > prev.issues || supervisorPendingReports > prev.reports;
          const shouldNotify = isFirstRefresh || hasIncrease;

          supervisorPrevRef.current = { overdue: supervisorOverdueSorties, pending: supervisorPendingSorties, issues: supervisorActiveIssues, reports: supervisorPendingReports, didNotify: true };

          const allowAlerts = isPageVisible();
          if (
            allowAlerts &&
            shouldNotify &&
            (supervisorOverdueSorties > 0 || supervisorPendingSorties > 0 || supervisorActiveIssues > 0 || supervisorPendingReports > 0)
          ) {
            const level = supervisorOverdueSorties > 0 ? 'warning' : 'info';
            let soundPromise = null;
            if (isFirstRefresh) {
              soundPromise = supervisorOverdueSorties > 0 ? playUrgentSound() : playWarningSound();
            } else if (!isFirstRefresh) {
              if (supervisorOverdueSorties > prev.overdue) soundPromise = playUrgentSound();
              else if (supervisorPendingSorties > prev.pending || supervisorActiveIssues > prev.issues || supervisorPendingReports > prev.reports) soundPromise = playWarningSound();
            }

            if (soundPromise) {
              try { await soundPromise; } catch { /* ignore */ }
            }
            notification[level]({
              message: 'Attention',
              description: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {supervisorOverdueSorties > 0 && <div>{supervisorOverdueSorties} sorties en retard</div>}
                  {supervisorPendingSorties > 0 && <div>{supervisorPendingSorties} sorties en attente</div>}
                  {supervisorActiveIssues > 0 && <div>{supervisorActiveIssues} signalements actifs</div>}
                  {supervisorPendingReports > 0 && <div>{supervisorPendingReports} rapports à traiter</div>}
                </div>
              ),
              placement: 'topRight',
              duration: Number(process.env.REACT_APP_SUPERVISOR_ALERT_DURATION_SEC || 30),
            });
          }

          if (!cancelled) setBadgeCounts((p) => ({ ...p, supervisorPendingSorties, supervisorOverdueSorties, supervisorActiveIssues, supervisorPendingReports }));
        }

        if (user.role === 'student') {
          const [sortiesRes, issuesRes] = await Promise.all([listMySortiesCount({ statut: 'PENDING' }), listMyIssuesCount({ statut_in: 'OPEN,IN_PROGRESS' })]);
          const studentPendingSorties = Number(sortiesRes?.total || 0);
          const studentActiveIssues = Number(issuesRes?.total || 0);
          const reportsRes = await listMyRoomReportsCount();
          const studentPendingReports = Number(reportsRes?.total || 0);
          if (!cancelled) setBadgeCounts((p) => ({ ...p, studentPendingSorties, studentActiveIssues, studentPendingReports }));
        }
      } catch { /* ignore */ }
    };

    refresh();
    const t = setInterval(refresh, refreshMs);
    const onImmediate = () => refresh();
    const onVisibility = () => {
      if (isPageVisible()) refresh();
    };
    window.addEventListener('badges:refresh', onImmediate);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener('badges:refresh', onImmediate);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.role]);

  const go = (path) => {
    navigate(path);
    setNavOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const displayName = user?.nom ? `${user.prenom || ''} ${user.nom}`.trim() : user?.email || '';

  const navLinks = useMemo(() => {
    if (!user) return [];
    if (user.role === 'admin') return [
      { path: '/admin', icon: <HomeIcon />, label: 'Dashboard' },
      { path: '/admin/weekend-stats', icon: <CalendarIcon />, label: 'Stats week-end' },
      { path: '/admin/students', icon: <UsersIcon />, label: 'Étudiants' },
      { path: '/admin/student-import', icon: <UsersIcon />, label: 'Whitelist Étudiants' },
      { path: '/admin/residences', icon: <BuildingIcon />, label: 'Résidences' },
      { path: '/admin/supervisors', icon: <UsersIcon />, label: 'Superviseurs' },
      { path: '/admin/semestres', icon: <CalendarIcon />, label: 'Semestres' },
      { path: '/admin/admin-accounts', icon: <UsersIcon />, label: 'Comptes admin' },
      { path: '/admin/ai-knowledge', icon: <BotNavIcon />, label: 'Assistant IA' },
      { path: '/admin/profile', icon: <ProfileIcon />, label: 'Mon profil' },
    ];
    if (user.role === 'supervisor') return [
      { path: '/supervisor', icon: <HomeIcon />, label: 'Dashboard' },
      { path: '/supervisor/students', icon: <UsersIcon />, label: 'Étudiants' },
      { path: '/supervisor/rooms', icon: <BuildingIcon />, label: 'Chambres' },
      { path: '/supervisor/sorties', icon: <ListIcon />, label: 'Sorties', redBadge: badgeCounts.supervisorOverdueSorties, orangeBadge: badgeCounts.supervisorPendingSorties },
      { path: '/supervisor/issues', icon: <ListIcon />, label: 'Signalements', orangeBadge: badgeCounts.supervisorActiveIssues },
      { path: '/supervisor/reports', icon: <ListIcon />, label: 'Rapports', orangeBadge: badgeCounts.supervisorPendingReports },
      { path: '/supervisor/evaluations', icon: <ListIcon />, label: 'Évaluations' },
    ];
    if (user.role === 'student') return [
      { path: '/student', icon: <HomeIcon />, label: 'Mon espace' },
      { path: '/student/sorties', icon: <ListIcon />, label: 'Mes sorties', orangeBadge: badgeCounts.studentPendingSorties },
      { path: '/student/new-sortie', icon: <ListIcon />, label: 'Nouvelle sortie' },
      { path: '/student/reports', icon: <ListIcon />, label: 'Mes rapports', orangeBadge: badgeCounts.studentPendingReports },
      { path: '/student/scan-qr', icon: <QrCodeIcon />, label: 'Scanner QR superviseur' },
      { path: '/student/issues', icon: <ListIcon />, label: 'Mes signalements', orangeBadge: badgeCounts.studentActiveIssues },
    ];
    return [];
  }, [user, badgeCounts]);

  return (
    <div className="app-shell">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <button className="hamburger" onClick={() => setNavOpen((v) => !v)} aria-label="Menu">
            <HamburgerIcon />
          </button>
          <span className="header-logo">IN/OUT</span>
          <span className="header-name">{displayName}</span>
        </div>
        <div className="header-right">
          <button className="header-logout" onClick={handleLogout}>
            <LogoutIcon />
            Déconnexion
          </button>
        </div>
      </header>

      <div className="app-body">
        {/* Mobile overlay */}
        <div className={`nav-overlay${navOpen ? ' open' : ''}`} onClick={() => setNavOpen(false)} />

        {/* Sidebar nav */}
        <nav className={`nav-sidebar${navOpen ? ' open' : ''}`}>
          <div className="nav-sidebar-title">Navigation</div>
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              path={link.path}
              icon={link.icon}
              label={link.label}
              redBadge={link.redBadge || 0}
              orangeBadge={link.orangeBadge || 0}
              currentPath={location.pathname}
              onClick={go}
            />
          ))}
        </nav>

        {/* Page content */}
        <main className="app-content">
          <Outlet />
        </main>
      </div>

      {/* AI Chatbot — visible uniquement pour les étudiants */}
      <AiChatbot />
    </div>
  );
}
